// daily 分支下的「基线版本」选择器:
// 主输入(Input)+ 右侧「历史」按钮打开表格弹窗;弹窗里 200 条历史 versionNo 可搜索、
// 可排序、点行回填,末列「追溯」可就地查该版本各仓 commit / 基线链(不改表单值)。
// 避免了老的 AutoComplete 拿"当前选中值"当过滤词造成"只看到自己"的反直觉行为。
//
// 设计基调:内部工具,精工小仪表盘 —— 版本号等宽字体、当前选中态清晰(左侧蓝条)、
// 搜索匹配子串高亮、每行 44px、虚拟滚动到 200 条不卡。

import { useMemo, useState, type ReactNode } from 'react';
import { Modal, Input, Button, Table, Empty, Tag, message } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { DailyVersionItem } from '../../apis/simulation';
import { ResizableTitle, useResizableColumns } from '../ResizableTable';
import { useVersionTrace } from '../VersionTraceModal/useVersionTrace';

/** DailyTagPicker 高亮引导事件 —— 当用户在未选 tag 时点了「历史」,
 *  Picker 广播这个 CustomEvent,DailyTagPicker 监听后短暂高亮 Select。 */
export const DAILY_TAG_HINT_EVENT = 'daily-tag-picker-hint';

/** 版本号用的等宽字体栈,统一放这里方便复用。 */
const MONO_STACK =
  '"JetBrains Mono", "Fira Code", "SF Mono", Menlo, Consolas, monospace';

/** 历史弹窗的叠放层级。显式钉住(而不是吃 antd 默认 1000),这样上面的追溯窗
 *  只要 +10 就稳定压住它,不必依赖两者 portal 的挂载先后。 */
const HISTORY_MODAL_Z_INDEX = 1000;

/** 时间列显示:后端返回 "2026-08-26T14:35:22" 之类,截到秒并把 T 换空格。空返回破折号。 */
function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return iso.slice(0, 19).replace('T', ' ');
}

/** 高亮工具:把 text 里所有(不区分大小写)匹配 needle 的子串包成 <mark>。 */
function highlight(text: string, needle: string): ReactNode {
  if (!needle) return text;
  const q = needle.trim().toLowerCase();
  if (!q) return text;
  const parts: ReactNode[] = [];
  let i = 0;
  const lower = text.toLowerCase();
  while (i < text.length) {
    const idx = lower.indexOf(q, i);
    if (idx < 0) {
      parts.push(text.slice(i));
      break;
    }
    if (idx > i) parts.push(text.slice(i, idx));
    parts.push(
      <mark
        key={`m-${idx}`}
        style={{
          backgroundColor: '#fff7bf',
          color: 'inherit',
          padding: '0 2px',
          borderRadius: 2,
        }}
      >
        {text.slice(idx, idx + q.length)}
      </mark>,
    );
    i = idx + q.length;
  }
  return <>{parts}</>;
}

interface DailyBaseVersionHistoryModalProps {
  open: boolean;
  onClose: () => void;
  onPick: (versionNo: string) => void;
  candidates: DailyVersionItem[];
  /** 当前 form 里 baseVersion 的值,用于给对应行加"当前"高亮。 */
  currentValue?: string;
  /** 点某行「追溯」:上抛版本号给父组件开 VersionTraceModal。父组件持有该 Modal,
   *  这样本弹窗 destroyOnClose 卸载时追溯窗不会跟着被销毁。 */
  onTrace: (versionNo: string) => void;
}

/** 历史列表弹窗。300 条数据用 antd Table 客户端搜索/排序,按 createTime DESC 默认。 */
function DailyBaseVersionHistoryModal({
  open,
  onClose,
  onPick,
  candidates,
  currentValue,
  onTrace,
}: DailyBaseVersionHistoryModalProps) {
  const [q, setQ] = useState('');

  // 列宽可拖动。3 列去掉了「基线版本」「来源 tag」,新增「编译状态」,弹窗收窄到 720。
  // 末列「追溯」是定宽操作列,不参与拖动。
  const { columnWidths, resizeHeaderCell } = useResizableColumns({
    versionNo: 340,
    buildStatus: 120,
    createTime: 160,
  });

  // 去重(同一 versionNo 罕见但不排除)、按 createTime DESC(后端已排好,这里再兜底)。
  const rows = useMemo(() => {
    const map = new Map<string, DailyVersionItem>();
    for (const v of candidates) {
      if (!v.versionNo) continue;
      if (!map.has(v.versionNo)) map.set(v.versionNo, v);
    }
    return Array.from(map.values()).sort((a, b) =>
      (b.createTime || '').localeCompare(a.createTime || ''),
    );
  }, [candidates]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((v) => {
      const bag = [
        v.versionNo,
        v.createTime,
        v.statusName,
        String(v.jobId ?? ''),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return bag.includes(query);
    });
  }, [rows, q]);

  const columns: ColumnsType<DailyVersionItem> = [
    {
      title: '版本号',
      dataIndex: 'versionNo',
      key: 'versionNo',
      width: columnWidths.versionNo,
      onHeaderCell: resizeHeaderCell('versionNo'),
      ellipsis: true,
      sorter: (a, b) => (a.versionNo || '').localeCompare(b.versionNo || ''),
      render: (v: string, row) => {
        const isCurrent = !!currentValue && v === currentValue;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                fontFamily: MONO_STACK,
                fontSize: 13,
                fontWeight: isCurrent ? 600 : 500,
                color: isCurrent ? '#1677ff' : '#1f1f1f',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
              }}
            >
              {highlight(v, q)}
            </span>
            {isCurrent && (
              <Tag
                color="blue"
                style={{
                  margin: 0,
                  fontSize: 11,
                  lineHeight: '16px',
                  padding: '0 6px',
                  borderRadius: 3,
                  flexShrink: 0,
                }}
              >
                当前
              </Tag>
            )}
            {row.jobId ? (
              <span
                style={{
                  fontSize: 11,
                  color: '#8c8c8c',
                  fontFamily: MONO_STACK,
                  flexShrink: 0,
                }}
              >
                #{row.jobId}
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      title: '编译状态',
      key: 'buildStatus',
      width: columnWidths.buildStatus,
      onHeaderCell: resizeHeaderCell('buildStatus'),
      ellipsis: true,
      sorter: (a, b) => {
        // 按"编译成功优先 → 编译中 → 编译失败 → 未知"排序
        const rank = (s: number | null | undefined) =>
          s == null ? 3 : s === 35 ? 2 : s === 7 ? 1 : 0;
        return rank(a.status) - rank(b.status);
      },
      render: (_: unknown, row) => {
        const s = row.status;
        // 语义收敛到用户在意的三档:
        //   compile_failed(35) → 编译失败(红)
        //   compiling(7)       → 编译中(蓝)
        //   其余 ≥ pending     → 编译成功(绿) —— 无论仿真结果如何,产物都在
        if (s == null) return <span style={{ color: '#bfbfbf' }}>—</span>;
        if (s === 35) return <Tag color="red">编译失败</Tag>;
        if (s === 7) return <Tag color="processing">编译中</Tag>;
        return <Tag color="success">编译成功</Tag>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: columnWidths.createTime,
      onHeaderCell: resizeHeaderCell('createTime'),
      ellipsis: true,
      defaultSortOrder: 'descend',
      sorter: (a, b) =>
        (a.createTime || '').localeCompare(b.createTime || ''),
      render: (v: string | null) => (
        <span style={{ fontSize: 12, color: '#8c8c8c' }}>{fmtTime(v)}</span>
      ),
    },
    {
      title: '操作',
      key: 'trace',
      width: 76,
      fixed: 'right',
      render: (_: unknown, row) =>
        row.versionNo ? (
          // stopPropagation:行本身的 onClick 是「选中该版本并关弹窗」,不拦的话
          // 点「追溯」会连带把版本填进表单并把这个弹窗关掉。追溯是纯查看动作,
          // 不应该改表单值。
          <Button
            type="link"
            size="small"
            style={{ padding: 0, fontSize: 12 }}
            onClick={(e) => {
              e.stopPropagation();
              onTrace(row.versionNo as string);
            }}
          >
            追溯
          </Button>
        ) : null,
    },
  ];

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span>选择基线版本</span>
          <span style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 400 }}>
            共 {rows.length} 条历史 daily 打版
          </span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      zIndex={HISTORY_MODAL_Z_INDEX}
      destroyOnClose
      styles={{ body: { paddingTop: 12 } }}
    >
      <Input.Search
        allowClear
        placeholder="搜索版本号 / 编译状态 / jobId"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{ marginBottom: 12 }}
      />

      {filtered.length === 0 ? (
        <Empty
          description={
            rows.length === 0
              ? '该 Tag 下没有 daily 打版历史'
              : `未匹配 "${q}"`
          }
          style={{ padding: '48px 0' }}
        />
      ) : (
        <Table<DailyVersionItem>
          size="small"
          rowKey="versionNo"
          columns={columns}
          dataSource={filtered}
          components={{ header: { cell: ResizableTitle } }}
          pagination={{
            pageSize: 20,
            showSizeChanger: false,
            size: 'small',
            showTotal: (t) => `${t} 条`,
          }}
          scroll={{ y: 400, x: 'max-content' }}
          onRow={(row) => ({
            onClick: () => {
              if (row.versionNo) {
                onPick(row.versionNo);
                onClose();
              }
            },
            style: { cursor: 'pointer' },
          })}
          rowClassName={(row) =>
            currentValue && row.versionNo === currentValue
              ? 'daily-history-current-row'
              : ''
          }
        />
      )}

      {/* 当前行用左侧蓝条 + 淡蓝底,一眼看到"我上次选的" */}
      <style>{`
        .daily-history-current-row td {
          background-color: #e6f4ff !important;
          box-shadow: inset 2px 0 0 0 #1677ff;
        }
      `}</style>
    </Modal>
  );
}

interface DailyBaseVersionPickerProps {
  value?: string;
  onChange?: (value: string) => void;
  candidates: DailyVersionItem[];
  /** 是否已选 Daily Tag。未选时点「历史」不开弹窗,而是引导用户上去选 tag。
   *  显式传入而不是靠 candidates.length 推断,避免异步 / clear 边界状态错判。 */
  tagSelected: boolean;
  /** 输入框右侧探针状态图标(比如"该版本存在/不存在") */
  suffix?: ReactNode;
  /** input placeholder */
  placeholder?: string;
}

/** 主组件:Input + 侧边"历史"按钮。value / onChange 走 Form.Item 的受控接口。
 *
 *  引导:未选 Daily Tag 时(tagSelected=false),按钮**不**置灰 —— 点它就弹 message
 *  提示 + 广播 DAILY_TAG_HINT_EVENT 让 DailyTagPicker 高亮 Select 一次。这比灰色
 *  disabled 更醒目,把"你缺什么"直接说清楚,而不是"这里不能点"。 */
export default function DailyBaseVersionPicker({
  value,
  onChange,
  candidates,
  tagSelected,
  suffix,
  placeholder,
}: DailyBaseVersionPickerProps) {
  const [modalOpen, setModalOpen] = useState(false);
  // 追溯 Modal 持有在这一层(而非历史弹窗内):历史弹窗 destroyOnClose,若挂在里面,
  // 追溯窗会随它卸载。放这里则两者独立,关掉历史列表也能继续看追溯结果。
  //
  // zIndex 必须显式抬高:antd Modal 默认都是 1000,同层级时靠 portal 在 body 里的
  // 先后顺序决定谁在上。历史弹窗带 destroyOnClose —— 每次重开都会把自己的 portal
  // 重新 append 到 body 末尾,于是排到先挂载的追溯窗后面、把它盖住(所以是"第一次
  // 正常,换个版本再开就被遮"的偶现表现)。给追溯窗 +10 让叠放不再依赖挂载顺序。
  const { onTrace, modal: versionTraceModal } = useVersionTrace(HISTORY_MODAL_Z_INDEX + 10);

  const handleHistoryClick = () => {
    if (!tagSelected) {
      message.info({
        content: '请先在上方选择 Daily Tag,再查看该 tag 下的更多版本',
        duration: 2.5,
      });
      window.dispatchEvent(new CustomEvent(DAILY_TAG_HINT_EVENT));
      return;
    }
    setModalOpen(true);
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
        <Input
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          suffix={suffix}
          allowClear
          style={{ flex: 1 }}
        />
        <Button
          icon={<HistoryOutlined />}
          onClick={handleHistoryClick}
          title={
            !tagSelected
              ? '请先在上方选择一个 Daily Tag,拉取该 tag 下的更多版本'
              : `查看 ${candidates.length} 条历史 daily 打版`
          }
        >
          更多版本{tagSelected && candidates.length > 0 ? ` (${candidates.length})` : ''}
        </Button>
      </div>

      <DailyBaseVersionHistoryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onPick={(v) => onChange?.(v)}
        candidates={candidates}
        currentValue={value}
        onTrace={onTrace}
      />
      {versionTraceModal}
    </>
  );
}
